import { Flex } from '@chakra-ui/react';
import {
    ColorModeButton,
    HelpMenu,
    insetRailProps,
    Sidebar,
} from '@ionq/legacy-ui';
import { PAGE_ICONS_MAP } from '../lib/pageIcons';
import { useHasSwitchTarget } from './OrganizationSwitcherMenu';
import { OrgContextRow } from './OrgContextRow';
import { useSession } from './SessionProvider';

type AppSidebarProps = React.ComponentProps<typeof Sidebar>;

// The full ionq-cloud-ui menu flattened into one view: the legacy app splits
// it into a main sidebar and a settings sidebar (with a "Return Home" item);
// here every destination is visible at once, so no mode switch is needed.

export default function AppSidebar(props: AppSidebarProps) {
    const menuPositioning = Sidebar.useFlyout('top-start');
    // Org pages are id-less — always the session's own organization (the
    // remote derives it from the same cookie).
    const { currentOrganization } = useSession();
    const orgBase = '/organization';
    // Same gate as ionq-cloud-ui's own sidebar: Settings/Reporting/Spend
    // are owner-only (the remote's ownerLoader bounces non-owners to /jobs
    // anyway); Members is the one org page every member can open.
    const isOwner = currentOrganization.userRole === 'owner';
    // The session is loaded before the layout renders, so this is settled
    // on first paint — the band never flips between the two brands.
    const hasSwitchTarget = useHasSwitchTarget();

    return (
        <Sidebar {...insetRailProps} {...props}>
            {/* Column: brand band, scrollable nav, pinned footer. The
                Sidebar's panel is a plain block, so the column lives here. */}
            <Flex direction='column' height='100%'>
                {/* Brand band: with another org to switch to, the mark
                    alone with the org switcher beside it (sidebar-07's
                    team-switcher slot, folded into the band); with a single
                    membership the full wordmark — nothing to switch, so no
                    row. */}
                <Sidebar.Brand wordmark={!hasSwitchTarget}>
                    {hasSwitchTarget && <OrgContextRow />}
                </Sidebar.Brand>
                <Sidebar.Nav>
                    {/* Identity lives in the top bar (AppNavbar), not in the
                        rail. */}
                    <Sidebar.ItemGroup>
                        <Sidebar.ItemLink
                            // Canonical URL; NavLink to='/' never highlights.
                            to='/dashboard'
                            label='Home'
                            icon={PAGE_ICONS_MAP.HOME}
                        />
                        <Sidebar.ItemLink
                            to='/jobs'
                            label='My Jobs'
                            icon={PAGE_ICONS_MAP.MY_JOBS}
                        />
                        <Sidebar.ItemLink
                            to='/projects'
                            label='Projects'
                            icon={PAGE_ICONS_MAP.PROJECTS}
                        />
                        <Sidebar.ItemLink
                            to='/settings/keys'
                            label='API Keys'
                            icon={PAGE_ICONS_MAP.API_KEYS}
                        />
                    </Sidebar.ItemGroup>
                    <Sidebar.ItemGroup title='Organization'>
                        <Sidebar.ItemLink
                            to='/backends'
                            label='Backends'
                            icon={PAGE_ICONS_MAP.BACKENDS}
                        />
                        <Sidebar.ItemLink
                            to={`${orgBase}/members`}
                            label='Members'
                            icon={PAGE_ICONS_MAP.ORG_MEMBERS}
                        />
                        {isOwner && (
                            <Sidebar.ItemLink
                                to={`${orgBase}/reporting`}
                                label='Reporting'
                                icon={PAGE_ICONS_MAP.USAGE_REPORTING}
                            />
                        )}
                        {isOwner && (
                            <Sidebar.ItemLink
                                to={`${orgBase}/spend`}
                                label='Spend'
                                icon={PAGE_ICONS_MAP.SPEND}
                            />
                        )}
                        {isOwner && (
                            <Sidebar.ItemLink
                                to={`${orgBase}/settings`}
                                label='Settings'
                                // Disambiguated from the account menu's
                                // Account Settings in the mini rail.
                                tooltip='Organization Settings'
                                icon={PAGE_ICONS_MAP.SETTINGS}
                            />
                        )}
                    </Sidebar.ItemGroup>
                </Sidebar.Nav>
                {/* Compact xs icon controls only — the docs/support links
                    live in the ?-menu; identity and the invites bell in the
                    top bar (sidebar-01). [color mode] [support] ---- [toggle]:
                    the collapse toggle (the footer's default far-edge
                    control) over the rail it folds. */}
                <Sidebar.FooterControls>
                    <ColorModeButton size='xs' />
                    <HelpMenu positioning={menuPositioning} />
                </Sidebar.FooterControls>
            </Flex>
        </Sidebar>
    );
}
